import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, MessageCircle, Building2, Phone } from 'lucide-react';
import { SUPPORT_PHONE, SUPPORT_PHONE_HREF } from '../config/contact';

const ContactPage = () => {
  const supportOptions = [
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: 'General Inquiries',
      email: 'support@clearway-mobility.com',
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: 'Shipping & Orders',
      email: 'orders@clearway-mobility.com',
    },
    {
      icon: <Building2 className="w-5 h-5" />,
      title: 'Partnerships',
      email: 'partner@clearway-mobility.com',
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-gray-400">
            Have questions? We&apos;re here to help.
          </p>
        </motion.div>

        {/* Office Address */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-8 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 flex-shrink-0">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Office Address</h3>
              <p className="text-white font-medium">Clearway Mobility LLP</p>
              <p className="text-gray-400">S-8, Pemino, Altamount Road, Cumballa Hill</p>
              <p className="text-gray-400">Mumbai 400026, Maharashtra, India</p>
              <p className="mt-3">
                <a
                  href={SUPPORT_PHONE_HREF}
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {SUPPORT_PHONE}
                </a>
              </p>
              <p className="text-gray-500 text-sm mt-2">LLP Identification Number: ADA-2903</p>
            </div>
          </div>
        </motion.div>

        {/* Support Options */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Get in Touch</h2>

          {/* Phone support — the fastest route for anyone who cannot use email */}
          <a
            href={SUPPORT_PHONE_HREF}
            className="flex items-center gap-4 p-5 mb-4 rounded-2xl bg-gray-800/30 border border-gray-800/50 hover:border-blue-500/30 transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500/20 transition-colors flex-shrink-0">
              <Phone className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold">Call Support</h3>
              <p className="text-blue-400 text-sm">{SUPPORT_PHONE}</p>
            </div>
          </a>

          <div className="grid md:grid-cols-3 gap-4">
            {supportOptions.map((option) => (
              <a
                key={option.title}
                href={`mailto:${option.email}`}
                className="p-5 rounded-2xl bg-gray-800/30 border border-gray-800/50 hover:border-blue-500/30 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500/20 transition-colors">
                    {option.icon}
                  </div>
                  <h3 className="font-semibold">{option.title}</h3>
                </div>
                <p className="text-blue-400 text-sm">{option.email}</p>
              </a>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20"
        >
          <h2 className="text-2xl font-bold mb-4">About ClearWay</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            ClearWay is a product by <strong className="text-white">Clearway Mobility LLP</strong>, founded by
            Jaiveer Talwar. We design assistive technology products
            that enhance independence and confidence for visually impaired and elderly users.
          </p>
          <p className="text-gray-400 leading-relaxed">
            Our smart walking stick combines cutting-edge sensor technology with elegant design 
            to provide reliable obstacle detection, surface sensing, and location tracking.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
